import { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { BookOpen, FileText, ChevronLeft, ChevronRight, ZoomIn, Maximize, Minimize } from 'lucide-react';
import styles from './PdfViewer.module.css';

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.mjs',
  import.meta.url
).toString();

interface TextMatch {
  pageIndex: number;
  itemIndex: number;
  text: string;
}

export interface PdfViewerHandle {
  search: (query: string) => void;
  nextMatch: () => void;
  prevMatch: () => void;
  clearSearch: () => void;
}

interface PdfViewerProps {
  url: string;
  searchQuery?: string;
  onSearchResults?: (total: number, current: number) => void;
}

const pdfCache = new Map<string, ArrayBuffer>();

const PdfViewer = forwardRef<PdfViewerHandle, PdfViewerProps>(({ url, searchQuery = '', onSearchResults }, ref) => {
  const [pdf, setPdf] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [pageCount, setPageCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [loadProgress, setLoadProgress] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [renderedPages, setRenderedPages] = useState<Map<number, boolean>>(new Map());
  const [defaultPageSize, setDefaultPageSize] = useState({ width: 600, height: 800 });
  const [matches, setMatches] = useState<TextMatch[]>([]);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(-1);
  const [textContents, setTextContents] = useState<Map<number, any[]>>(new Map());
  const [viewMode, setViewMode] = useState<'single' | 'double'>('double');
  const [calculatedScale, setCalculatedScale] = useState(1.0);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [magnifierActive, setMagnifierActive] = useState(false);
  const [magnifierPos, setMagnifierPos] = useState({ x: 0, y: 0 });
  const [magnifierVisible, setMagnifierVisible] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const magnifierCanvasRef = useRef<HTMLCanvasElement>(null);
  const magnifierPageRef = useRef<number>(0);
  const rafRef = useRef<number>(0);
  const viewerRef = useRef<HTMLDivElement>(null);
  
  const canvasRefs = useRef<Map<number, HTMLCanvasElement>>(new Map());
  const textLayerRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const renderingRef = useRef<Set<number>>(new Set());
  const contentAreaRef = useRef<HTMLDivElement>(null);
  const [renderTrigger, setRenderTrigger] = useState(0);

  useEffect(() => {
    const loadPdf = async () => {
      try {
        setIsLoading(true);
        setLoadProgress(0);
        setRenderedPages(new Map());
        setTextContents(new Map());
        setCurrentPage(1);
        canvasRefs.current.clear();
        textLayerRefs.current.clear();
        renderingRef.current.clear();
        
        let pdfData: ArrayBuffer;
        
        if (pdfCache.has(url)) {
          pdfData = pdfCache.get(url)!;
          setLoadProgress(100);
        } else {
          const response = await fetch(url);
          const reader = response.body?.getReader();
          const contentLength = parseInt(response.headers.get('Content-Length') || '0', 10);
          
          if (reader && contentLength > 0) {
            const chunks: Uint8Array[] = [];
            let receivedLength = 0;
            
            while (true) {
              const { done, value } = await reader.read();
              if (done) break;
              
              chunks.push(value);
              receivedLength += value.length;
              setLoadProgress(Math.round((receivedLength / contentLength) * 100));
            }
            
            pdfData = new Uint8Array(receivedLength);
            let position = 0;
            for (const chunk of chunks) {
              (pdfData as Uint8Array).set(chunk, position);
              position += chunk.length;
            }
            pdfData = (pdfData as Uint8Array).buffer;
          } else {
            pdfData = await response.arrayBuffer();
            setLoadProgress(100);
          }
          
          pdfCache.set(url, pdfData);
        }
        
        const loadingTask = pdfjsLib.getDocument({
          data: pdfData.slice(0),
          cMapUrl: 'https://unpkg.com/pdfjs-dist@4.4.168/cmaps/',
          cMapPacked: true,
          maxImageSize: 1024 * 1024 * 100, // 100MB image size limit for large full-page images
          isEvalSupported: true,
          useSystemFonts: true,
          enableXfa: true,
        });
        
        const pdfDoc = await loadingTask.promise;
        setPdf(pdfDoc);
        setPageCount(pdfDoc.numPages);
        
        const firstPage = await pdfDoc.getPage(1);
        const viewport = firstPage.getViewport({ scale: 1.0 }); // Use scale 1.0 to get base dimensions
        setDefaultPageSize({ width: viewport.width, height: viewport.height });
        
        setIsLoading(false);
      } catch (error: any) {
        console.error('Error loading PDF:', error);
        setIsLoading(false);
      }
    };

    if (url) {
      loadPdf();
    }
  }, [url]);

  // Measure container size and recalculate on resize
  useEffect(() => {
    const measureContainer = () => {
      if (contentAreaRef.current) {
        const rect = contentAreaRef.current.getBoundingClientRect();
        // Account for nav arrows (48px each) and gaps
        const availableWidth = rect.width - 120;
        const availableHeight = rect.height - 20;
        setContainerSize({ width: availableWidth, height: availableHeight });
      }
    };

    measureContainer();
    window.addEventListener('resize', measureContainer);
    return () => window.removeEventListener('resize', measureContainer);
  }, []);

  // Calculate optimal scale based on page size and container size
  useEffect(() => {
    if (defaultPageSize.width === 0 || containerSize.width === 0) return;

    const { width: pageWidth, height: pageHeight } = defaultPageSize;
    const { width: containerWidth, height: containerHeight } = containerSize;

    let targetWidth: number;
    let targetHeight: number;

    if (viewMode === 'double') {
      // For book mode, fit two pages side by side
      targetWidth = containerWidth / 2 - 4; // Small gap between pages
      targetHeight = containerHeight;
    } else {
      // For single page, use full width
      targetWidth = containerWidth;
      targetHeight = containerHeight;
    }

    // Calculate scale to fit within both width and height constraints
    const scaleByWidth = targetWidth / pageWidth;
    const scaleByHeight = targetHeight / pageHeight;
    const optimalScale = Math.min(scaleByWidth, scaleByHeight, 2.0); // Cap at 2x for quality

    // Only update if scale changed significantly
    if (Math.abs(optimalScale - calculatedScale) > 0.05) {
      setCalculatedScale(Math.max(0.3, optimalScale)); // Minimum 0.3x scale
      
      // Clear rendered pages to force re-render at new scale
      canvasRefs.current.clear();
      textLayerRefs.current.clear();
      renderingRef.current.clear();
      setRenderedPages(new Map());
      setRenderTrigger(prev => prev + 1);
    }
  }, [defaultPageSize, containerSize, viewMode, calculatedScale]);

  const renderPage = useCallback(async (pageNum: number, canvas: HTMLCanvasElement, textLayer: HTMLDivElement) => {
    if (!pdf || renderingRef.current.has(pageNum)) return;
    
    renderingRef.current.add(pageNum);

    try {
      const page = await pdf.getPage(pageNum);
      const viewport = page.getViewport({ scale: calculatedScale });

      // Use pixel ratio 1 for consistent rendering
      const outputScale = 1;
      
      canvas.width = Math.floor(viewport.width * outputScale);
      canvas.height = Math.floor(viewport.height * outputScale);
      canvas.style.width = `${Math.floor(viewport.width)}px`;
      canvas.style.height = `${Math.floor(viewport.height)}px`;

      const context = canvas.getContext('2d', { alpha: false });
      if (!context) {
        renderingRef.current.delete(pageNum);
        return;
      }

      // Fill with white background
      context.fillStyle = 'white';
      context.fillRect(0, 0, canvas.width, canvas.height);

      // Force preload all resources (images) before rendering
      try {
        await page.getOperatorList();
      } catch (e) {
        console.warn('Failed to preload page resources:', e);
      }

      const renderTask = page.render({
        canvasContext: context,
        viewport: viewport,
        intent: 'display',
      });
      
      await renderTask.promise;

      const textContent = await page.getTextContent();
      const items = textContent.items
        .filter((item: any) => 'str' in item)
        .map((item: any) => ({
          str: item.str,
          transform: item.transform,
          width: item.width || 0,
          height: item.height || 0,
        }));

      setTextContents((prev) => {
        const updated = new Map(prev);
        updated.set(pageNum, items);
        return updated;
      });

      textLayer.innerHTML = '';
      textLayer.style.width = `${viewport.width}px`;
      textLayer.style.height = `${viewport.height}px`;

      items.forEach((item: any, itemIndex: number) => {
        if (!item.str.trim()) return;
        
        const tx = pdfjsLib.Util.transform(viewport.transform, item.transform);
        const span = document.createElement('span');
        span.textContent = item.str;
        span.style.left = `${tx[4]}px`;
        span.style.top = `${viewport.height - tx[5]}px`;
        span.style.fontSize = `${Math.abs(tx[0])}px`;
        span.style.position = 'absolute';
        span.style.whiteSpace = 'pre';
        span.style.color = 'transparent';
        span.dataset.pageIndex = String(pageNum - 1);
        span.dataset.itemIndex = String(itemIndex);
        textLayer.appendChild(span);
      });
      
      setRenderedPages((prev) => {
        const updated = new Map(prev);
        updated.set(pageNum, true);
        return updated;
      });
      renderingRef.current.delete(pageNum);
    } catch (error) {
      console.error(`Error rendering page ${pageNum}:`, error);
      renderingRef.current.delete(pageNum);
    }
  }, [pdf, calculatedScale, pageCount]);

  useEffect(() => {
    if (!pdf || isLoading) return;

    const timer = setTimeout(() => {
      const pagesToRender = viewMode === 'single' 
        ? [currentPage]
        : [currentPage, currentPage + 1].filter(p => p <= pageCount);

      pagesToRender.forEach(pageNum => {
        const canvas = canvasRefs.current.get(pageNum);
        const textLayer = textLayerRefs.current.get(pageNum);
        if (canvas && textLayer && !renderedPages.get(pageNum) && !renderingRef.current.has(pageNum)) {
          renderPage(pageNum, canvas, textLayer);
        }
      });
    }, 100);

    return () => clearTimeout(timer);
  }, [pdf, isLoading, currentPage, viewMode, pageCount, renderPage, renderedPages, renderTrigger]);

  const goToNextPage = () => {
    const step = viewMode === 'single' ? 1 : 2;
    const maxPage = viewMode === 'single' ? pageCount : pageCount - 1;
    const newPage = Math.min(currentPage + step, maxPage);
    
    // Clear canvas refs and rendered state for new pages to force re-render
    const pagesToRender = viewMode === 'single' ? [newPage] : [newPage, newPage + 1];
    pagesToRender.forEach(p => {
      canvasRefs.current.delete(p);
      textLayerRefs.current.delete(p);
      renderingRef.current.delete(p);
    });
    setRenderedPages(prev => {
      const updated = new Map(prev);
      pagesToRender.forEach(p => updated.delete(p));
      return updated;
    });
    
    setCurrentPage(newPage);
    setRenderTrigger(prev => prev + 1);
  };

  const goToPrevPage = () => {
    const step = viewMode === 'single' ? 1 : 2;
    const newPage = Math.max(currentPage - step, 1);
    
    // Clear canvas refs and rendered state for new pages to force re-render
    const pagesToRender = viewMode === 'single' ? [newPage] : [newPage, newPage + 1];
    pagesToRender.forEach(p => {
      canvasRefs.current.delete(p);
      textLayerRefs.current.delete(p);
      renderingRef.current.delete(p);
    });
    setRenderedPages(prev => {
      const updated = new Map(prev);
      pagesToRender.forEach(p => updated.delete(p));
      return updated;
    });
    
    setCurrentPage(newPage);
    setRenderTrigger(prev => prev + 1);
  };

  const handleViewModeChange = (mode: 'single' | 'double') => {
    if (mode === viewMode) return;
    
    let newPage = currentPage;
    if (mode === 'double' && currentPage % 2 === 0) {
      newPage = currentPage - 1;
    }
    
    // Clear all canvas refs and rendered state to force re-render
    canvasRefs.current.clear();
    textLayerRefs.current.clear();
    renderingRef.current.clear();
    setRenderedPages(new Map());
    
    setViewMode(mode);
    setCurrentPage(newPage);
    setRenderTrigger(prev => prev + 1);
  };

  const performSearch = useCallback((query: string) => {
    if (!query.trim()) {
      setMatches([]);
      setCurrentMatchIndex(-1);
      onSearchResults?.(0, 0);
      return;
    }

    const searchLower = query.toLowerCase();
    const foundMatches: TextMatch[] = [];

    textContents.forEach((items, pageNum) => {
      items.forEach((item, itemIndex) => {
        const text = item.str.toLowerCase();
        if (text.includes(searchLower)) {
          foundMatches.push({
            pageIndex: pageNum - 1,
            itemIndex,
            text: item.str,
          });
        }
      });
    });

    setMatches(foundMatches);
    const newIndex = foundMatches.length > 0 ? 0 : -1;
    setCurrentMatchIndex(newIndex);

    if (foundMatches.length > 0) {
      setCurrentPage(foundMatches[0].pageIndex + 1);
    }
    
    onSearchResults?.(foundMatches.length, newIndex >= 0 ? newIndex + 1 : 0);
  }, [textContents, onSearchResults]);

  useEffect(() => {
    if (searchQuery !== undefined) {
      const debounce = setTimeout(() => {
        performSearch(searchQuery);
      }, 300);
      return () => clearTimeout(debounce);
    }
  }, [searchQuery, performSearch]);

  useEffect(() => {
    textLayerRefs.current.forEach((textLayer) => {
      const spans = textLayer.querySelectorAll('span');
      spans.forEach((span) => {
        span.classList.remove(styles.highlight, styles.currentHighlight);
      });
    });

    matches.forEach((match, index) => {
      const textLayer = textLayerRefs.current.get(match.pageIndex + 1);
      if (textLayer) {
        const spans = textLayer.querySelectorAll('span');
        const targetSpan = spans[match.itemIndex];
        if (targetSpan) {
          targetSpan.classList.add(styles.highlight);
          if (index === currentMatchIndex) {
            targetSpan.classList.add(styles.currentHighlight);
          }
        }
      }
    });
  }, [matches, currentMatchIndex]);

  const goToNextMatch = useCallback(() => {
    if (matches.length === 0) return;
    const nextIndex = (currentMatchIndex + 1) % matches.length;
    setCurrentMatchIndex(nextIndex);
    setCurrentPage(matches[nextIndex].pageIndex + 1);
    onSearchResults?.(matches.length, nextIndex + 1);
  }, [matches, currentMatchIndex, onSearchResults]);

  const goToPrevMatch = useCallback(() => {
    if (matches.length === 0) return;
    const prevIndex = (currentMatchIndex - 1 + matches.length) % matches.length;
    setCurrentMatchIndex(prevIndex);
    setCurrentPage(matches[prevIndex].pageIndex + 1);
    onSearchResults?.(matches.length, prevIndex + 1);
  }, [matches, currentMatchIndex, onSearchResults]);

  const clearSearch = useCallback(() => {
    setMatches([]);
    setCurrentMatchIndex(-1);
    onSearchResults?.(0, 0);
  }, [onSearchResults]);

  useImperativeHandle(ref, () => ({
    search: performSearch,
    nextMatch: goToNextMatch,
    prevMatch: goToPrevMatch,
    clearSearch,
  }), [performSearch, goToNextMatch, goToPrevMatch, clearSearch]);

  const updateMagnifier = useCallback((pageNum: number, mouseX: number, mouseY: number) => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      const magnifierCanvas = magnifierCanvasRef.current;
      const sourceCanvas = canvasRefs.current.get(pageNum);
      if (!magnifierCanvas || !sourceCanvas) return;

      const ctx = magnifierCanvas.getContext('2d');
      if (!ctx) return;

      const zoomLevel = 3;
      const sw = magnifierCanvas.width / zoomLevel;
      const sh = magnifierCanvas.height / zoomLevel;

      let sx = mouseX - sw / 2;
      let sy = mouseY - sh / 2;

      sx = Math.max(0, Math.min(sx, sourceCanvas.width - sw));
      sy = Math.max(0, Math.min(sy, sourceCanvas.height - sh));

      ctx.clearRect(0, 0, magnifierCanvas.width, magnifierCanvas.height);
      ctx.drawImage(sourceCanvas, sx, sy, sw, sh, 0, 0, magnifierCanvas.width, magnifierCanvas.height);
    });
  }, []);

  const handlePageMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>, pageNum: number) => {
    if (!magnifierActive) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setMagnifierPos({ x, y });
    setMagnifierVisible(true);
    magnifierPageRef.current = pageNum;
    updateMagnifier(pageNum, x, y);
  }, [magnifierActive, updateMagnifier]);

  const handlePageMouseLeave = useCallback(() => {
    if (!magnifierActive) return;
    setMagnifierVisible(false);
  }, [magnifierActive]);

  const toggleFullscreen = useCallback(() => {
    if (!viewerRef.current) return;
    if (!document.fullscreenElement) {
      viewerRef.current.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen().catch(() => {});
    }
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFull = !!document.fullscreenElement;
      setIsFullscreen(isFull);
      setTimeout(() => {
        if (contentAreaRef.current) {
          const rect = contentAreaRef.current.getBoundingClientRect();
          const availableWidth = rect.width - 120;
          const availableHeight = rect.height - 20;
          setContainerSize({ width: availableWidth, height: availableHeight });
        }
      }, 100);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const renderPageElement = (pageNum: number) => {
    if (pageNum > pageCount) return null;
    
    const scaledWidth = Math.floor(defaultPageSize.width * calculatedScale);
    const scaledHeight = Math.floor(defaultPageSize.height * calculatedScale);

    const lensTop = magnifierPos.y - 150 - 20 > 0
      ? magnifierPos.y - 150 - 20
      : magnifierPos.y + 20;
    
    return (
      <div 
        key={pageNum} 
        className={`${styles.pageWrapper} ${magnifierActive ? styles.magnifierCursor : ''}`}
        style={{ 
          minHeight: scaledHeight,
          minWidth: scaledWidth,
          width: scaledWidth,
          height: scaledHeight,
        }}
        onMouseMove={(e) => handlePageMouseMove(e, pageNum)}
        onMouseLeave={handlePageMouseLeave}
      >
        <canvas
          ref={(el) => {
            if (el) {
              canvasRefs.current.set(pageNum, el);
            }
          }}
          className={styles.pageCanvas}
          style={{ display: renderedPages.get(pageNum) ? 'block' : 'none' }}
        />
        <div
          ref={(el) => {
            if (el) {
              textLayerRefs.current.set(pageNum, el);
            }
          }}
          className={styles.textLayer}
          style={{ display: renderedPages.get(pageNum) ? 'block' : 'none' }}
        />
        {!renderedPages.get(pageNum) && (
          <div className={styles.pagePlaceholder}>
            <div className={styles.pageSpinner}></div>
            <span>Loading page {pageNum}...</span>
          </div>
        )}
        <div className={styles.pageNumber}>{pageNum}</div>
        {magnifierActive && magnifierVisible && magnifierPageRef.current === pageNum && (
          <div
            className={styles.magnifierLens}
            style={{
              left: magnifierPos.x - 200,
              top: lensTop,
            }}
          >
            <canvas
              ref={magnifierCanvasRef}
              width={800}
              height={600}
              className={styles.magnifierCanvas}
            />
          </div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner}></div>
        <p>Loading cookbook... {loadProgress > 0 ? `${loadProgress}%` : ''}</p>
      </div>
    );
  }

  const isFirstPage = currentPage === 1;
  const isLastPage = viewMode === 'single' 
    ? currentPage >= pageCount 
    : currentPage >= pageCount - 1;

  const getPageInfo = () => {
    if (viewMode === 'single') {
      return `Page ${currentPage} of ${pageCount}`;
    }
    const rightPage = Math.min(currentPage + 1, pageCount);
    return `Pages ${currentPage}-${rightPage} of ${pageCount}`;
  };

  return (
    <div ref={viewerRef} className={`${styles.viewer} ${isFullscreen ? styles.fullscreen : ''}`}>
      <div className={styles.toolbar}>
        <div className={styles.viewModeToggle}>
          <button
            className={`${styles.viewModeButton} ${viewMode === 'single' ? styles.active : ''}`}
            onClick={() => handleViewModeChange('single')}
            title="Single page view"
          >
            <FileText size={18} />
            <span>Single</span>
          </button>
          <button
            className={`${styles.viewModeButton} ${viewMode === 'double' ? styles.active : ''}`}
            onClick={() => handleViewModeChange('double')}
            title="Two page view"
          >
            <BookOpen size={18} />
            <span>Book</span>
          </button>
          <button
            className={`${styles.viewModeButton} ${magnifierActive ? styles.active : ''}`}
            onClick={() => { setMagnifierActive(!magnifierActive); setMagnifierVisible(false); }}
            title="Magnifier tool"
          >
            <ZoomIn size={18} />
            <span>Magnifier</span>
          </button>
          <button
            className={styles.viewModeButton}
            onClick={toggleFullscreen}
            title={isFullscreen ? 'Exit full screen' : 'Full screen'}
          >
            {isFullscreen ? <Minimize size={18} /> : <Maximize size={18} />}
            <span>{isFullscreen ? 'Exit' : 'Full Screen'}</span>
          </button>
        </div>
        
        <div className={styles.pageInfo}>{getPageInfo()}</div>
      </div>
      
      <div ref={contentAreaRef} className={styles.contentArea}>
        <button 
          className={styles.navArrow} 
          onClick={goToPrevPage}
          disabled={isFirstPage}
          aria-label="Previous page"
        >
          <ChevronLeft size={32} />
        </button>
        
        <div className={`${styles.pagesContainer} ${viewMode === 'double' ? styles.doublePageView : ''}`}>
          {viewMode === 'single' ? (
            renderPageElement(currentPage)
          ) : (
            <div className={styles.pageSpread}>
              {renderPageElement(currentPage)}
              {currentPage + 1 <= pageCount && renderPageElement(currentPage + 1)}
            </div>
          )}
        </div>
        
        <button 
          className={styles.navArrow} 
          onClick={goToNextPage}
          disabled={isLastPage}
          aria-label="Next page"
        >
          <ChevronRight size={32} />
        </button>
      </div>
    </div>
  );
});

PdfViewer.displayName = 'PdfViewer';

export default PdfViewer;
